import { useState, useEffect } from "react";
import api, { money, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ShoppingCart, Wrench, Search, Plus, Minus, Trash2, Printer, Clock, Zap,
  AlertTriangle, Receipt, History as HistoryIcon, X, DollarSign, Ban, TrendingUp,
} from "lucide-react";
import { PdvReceipt } from "@/components/PrintViews";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (p) => (!p ? "" : p.startsWith("http") ? p : `${BACKEND}${p}`);
const ProductThumb = ({ p, size = 40 }) => (
  <span className="rounded-md overflow-hidden bg-[#141418] border border-white/10 flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
    {p.photos?.[0]
      ? <img src={imgUrl(p.photos[0])} alt={p.name} className="h-full w-full object-cover" />
      : <ShoppingCart size={size * 0.4} className="text-zinc-600" />}
  </span>
);

export default function Pdv() {
  const { user, hasPerm } = useAuth();
  const [view, setView] = useState("main");
  const [now, setNow] = useState(new Date());
  const [saleOpen, setSaleOpen] = useState(false);
  const [osOpen, setOsOpen] = useState(false);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [gainOpen, setGainOpen] = useState(false);
  const [summaryKey, setSummaryKey] = useState(0);
  const [receipt, setReceipt] = useState(null);
  const [settings, setSettings] = useState({});

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data || {})); }, []);

  const printReceipt = (sale) => { setReceipt(sale); setTimeout(() => window.print(), 120); };

  return (
    <div className="space-y-6" data-testid="pdv-page">
      <div className="flex items-center justify-between flex-wrap gap-3 no-print">
        <div>
          <h1 className="text-3xl font-display font-extrabold tracking-tight text-white">PDV · Caixa</h1>
          <p className="text-zinc-400 mt-1 flex items-center gap-3 text-sm">
            <span>Operador: <span className="text-gold-bright">{user?.name}</span></span>
            <span className="inline-flex items-center gap-1 text-green-400">● Caixa aberto</span>
            <span className="inline-flex items-center gap-1"><Clock size={13} />{now.toLocaleTimeString("pt-BR")}</span>
          </p>
        </div>
        <div className="flex gap-2">
          {hasPerm("pdv.create") && <Button data-testid="btn-gain" variant="outline" onClick={() => setGainOpen(true)} className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"><TrendingUp size={15} className="mr-1" />Lançar Ganho</Button>}
          {hasPerm("pdv.create") && <Button data-testid="btn-expense" variant="outline" onClick={() => setExpenseOpen(true)} className="border-red-500/30 text-red-300 hover:bg-red-500/10"><DollarSign size={15} className="mr-1" />Lançar Custo</Button>}
          <Button data-testid="view-pdv" variant={view === "main" ? "default" : "outline"} onClick={() => setView("main")} className={view === "main" ? "text-black" : "border-white/15"} style={view === "main" ? { background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" } : {}}>PDV</Button>
          <Button data-testid="view-history" variant={view === "history" ? "default" : "outline"} onClick={() => setView("history")} className={view === "history" ? "text-black" : "border-white/15"} style={view === "history" ? { background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" } : {}}><HistoryIcon size={15} className="mr-1" />Histórico</Button>
        </div>
      </div>

      {view === "main" ? (
        <>
          <div className="grid sm:grid-cols-2 gap-4 no-print">
            <button data-testid="btn-new-sale" onClick={() => setSaleOpen(true)} className="alpha-card p-8 flex items-center gap-5 hover:translate-y-[-2px] text-left">
              <span className="h-16 w-16 rounded-2xl flex items-center justify-center text-black" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}><ShoppingCart size={28} /></span>
              <div><div className="text-xl font-display font-extrabold text-white">Nova Venda</div><div className="text-sm text-zinc-400">Venda rápida de produtos do estoque</div></div>
            </button>
            <button data-testid="btn-receive-os" onClick={() => setOsOpen(true)} className="alpha-card p-8 flex items-center gap-5 hover:translate-y-[-2px] text-left">
              <span className="h-16 w-16 rounded-2xl bg-gold/15 flex items-center justify-center"><Wrench size={28} className="text-gold" /></span>
              <div><div className="text-xl font-display font-extrabold text-white">Receber Ordem de Serviço</div><div className="text-sm text-zinc-400">Retirada do aparelho + venda adicional</div></div>
            </button>
          </div>
          <CashSummary key={summaryKey} />
        </>
      ) : (
        <SalesHistory onReprint={printReceipt} />
      )}

      {saleOpen && <NewSaleModal settings={settings} onClose={() => setSaleOpen(false)} onDone={(s) => { setSaleOpen(false); setSummaryKey((k) => k + 1); printReceipt(s); }} />}
      {osOpen && <ReceiveOSModal settings={settings} onClose={() => setOsOpen(false)} onDone={(s) => { setOsOpen(false); setSummaryKey((k) => k + 1); printReceipt(s); }} />}
      {expenseOpen && <ExpenseModal onClose={() => setExpenseOpen(false)} onDone={() => { setExpenseOpen(false); setSummaryKey((k) => k + 1); }} />}
      {gainOpen && <GainModal onClose={() => setGainOpen(false)} onDone={() => { setGainOpen(false); setSummaryKey((k) => k + 1); }} />}

      <div className="print-area hidden print:block">{receipt && <PdvReceipt sale={receipt} settings={settings} />}</div>
    </div>
  );
}

function useProducts() {
  const [products, setProducts] = useState([]);
  const [methods, setMethods] = useState([]);
  useEffect(() => {
    api.get("/pdv/products").then((r) => setProducts(r.data));
    api.get("/config/payment-methods").then((r) => setMethods(r.data));
  }, []);
  return { products, methods };
}

function Cart({ items, setItems, products }) {
  const add = (p) => setItems((c) => {
    const ex = c.find((i) => i.inventoryId === p.id);
    if (ex) return c.map((i) => i.inventoryId === p.id ? { ...i, qty: i.qty + 1 } : i);
    if (p.quantity <= (p.min_quantity || 0)) toast.warning(`Restam apenas ${p.quantity} un. de ${p.name}`);
    return [...c, { inventoryId: p.id, name: p.name, price: p.price, qty: 1, max: p.quantity }];
  });
  const setQty = (id, d) => setItems((c) => c.map((i) => i.inventoryId === id ? { ...i, qty: Math.max(1, i.qty + d) } : i));
  const remove = (id) => setItems((c) => c.filter((i) => i.inventoryId !== id));
  const featured = products.filter((p) => p.featured);
  return { add, setQty, remove, featured };
}

function ProductPicker({ products, onAdd, featured }) {
  const [q, setQ] = useState("");
  const list = products.filter((p) => (p.name + p.sku).toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        {featured.map((p) => (
          <button key={p.id} data-testid={`quick-prod-${p.sku}`} onClick={() => onAdd(p)} className="inline-flex items-center gap-1 rounded-full bg-gold/10 border border-gold/30 text-gold-bright px-3 py-1 text-xs hover:bg-gold/20">
            <Zap size={11} />{p.name} · {money(p.price)}
          </button>
        ))}
      </div>
      <div className="relative mb-2">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input data-testid="prod-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar produto por nome ou código..." className="i pl-9" />
      </div>
      <div className="max-h-72 overflow-y-auto space-y-1">
        {list.map((p) => (
          <button key={p.id} data-testid={`prod-${p.sku}`} onClick={() => onAdd(p)} className="w-full flex items-center gap-3 rounded-lg bg-[#0A0A0C] border border-white/5 px-3 py-2 text-sm hover:border-gold/30 text-left">
            <ProductThumb p={p} />
            <span className="flex-1 min-w-0">
              <span className="text-white flex items-center gap-2 truncate">{p.name}
                {p.quantity <= (p.min_quantity || 0) && <span className="inline-flex items-center gap-1 text-[10px] text-amber-400 shrink-0"><AlertTriangle size={10} />{p.quantity} un.</span>}
              </span>
              {p.sku && <span className="block text-[11px] text-zinc-500 truncate">Cód: {p.sku}</span>}
            </span>
            <span className="text-gold-bright font-mono-kpi shrink-0">{money(p.price)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function CartList({ items, setQty, remove }) {
  return (
    <div className="space-y-2" data-testid="cart-list">
      {items.length === 0 && <p className="text-xs text-zinc-500">Nenhum produto adicionado.</p>}
      {items.map((i) => (
        <div key={i.inventoryId} className="flex items-center gap-2 text-sm bg-[#0A0A0C] border border-white/5 rounded-lg px-3 py-2">
          <span className="flex-1 text-white truncate">{i.name}</span>
          <button onClick={() => setQty(i.inventoryId, -1)} className="text-zinc-400 hover:text-white"><Minus size={14} /></button>
          <span className="w-6 text-center font-mono-kpi">{i.qty}</span>
          <button onClick={() => setQty(i.inventoryId, 1)} className="text-zinc-400 hover:text-white"><Plus size={14} /></button>
          <span className="w-20 text-right text-gold-bright font-mono-kpi">{money(i.price * i.qty)}</span>
          <button onClick={() => remove(i.inventoryId)} className="text-zinc-500 hover:text-red-400"><Trash2 size={14} /></button>
        </div>
      ))}
    </div>
  );
}

function PayFooter({ methods, method, setMethod, total, discount, setDiscount, onFinish, saving, disabled }) {
  const net = Math.max(0, total - (parseFloat(discount) || 0));
  return (
    <DialogFooter className="flex-col sm:flex-row gap-3 items-stretch sm:items-center">
      <div className="mr-auto">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs text-zinc-400">Desconto R$</span>
          <Input data-testid="sale-discount" type="number" min="0" value={discount} onChange={(e) => setDiscount(e.target.value)} className="i h-8 w-24" placeholder="0,00" />
        </div>
        <div className="text-lg">Total: <span className="text-gold-bright font-extrabold font-mono-kpi" data-testid="sale-total">{money(net)}</span></div>
      </div>
      <Select value={method} onValueChange={setMethod}>
        <SelectTrigger data-testid="pay-method" className="i w-44"><SelectValue placeholder="Pagamento" /></SelectTrigger>
        <SelectContent className="bg-[#121217] border-gold/20 text-white">
          {methods.map((m) => <SelectItem key={m.key} value={m.key} className="focus:bg-gold/10">{m.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button data-testid="finish-sale" onClick={onFinish} disabled={saving || disabled || !method} className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>Finalizar</Button>
    </DialogFooter>
  );
}

function ClientPicker({ client, setClient }) {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => api.get(`/pdv/clients?q=${encodeURIComponent(q)}`).then((r) => setList(r.data)).catch(() => setList([])), 250);
    return () => clearTimeout(t);
  }, [q, open]);
  if (client) {
    return (
      <div className="flex items-center gap-2 bg-[#0A0A0C] border border-gold/30 rounded-lg px-3 py-2 text-sm" data-testid="pdv-client-selected">
        <span className="text-gold-bright">Cliente:</span>
        <span className="text-white flex-1 truncate">{client.name}{client.phone ? ` · ${client.phone}` : ""}</span>
        <button onClick={() => setClient(null)} data-testid="pdv-client-clear" className="text-zinc-500 hover:text-red-400"><X size={14} /></button>
      </div>
    );
  }
  return (
    <div>
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input data-testid="pdv-client-search" value={q} onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }} placeholder="Vincular cliente cadastrado (opcional)..." className="i pl-9" />
      </div>
      {open && list.length > 0 && (
        <div className="mt-1 max-h-40 overflow-y-auto space-y-1 border border-white/10 rounded-lg p-1 bg-[#0A0A0C]">
          {list.map((c) => (
            <button key={c.id} data-testid={`pdv-client-${c.id}`} onClick={() => { setClient(c); setOpen(false); setQ(""); }} className="w-full text-left px-3 py-1.5 rounded text-sm text-zinc-200 hover:bg-gold/10">
              {c.name}{c.phone ? <span className="text-zinc-500"> · {c.phone}</span> : ""}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NewSaleModal({ onClose, onDone }) {
  const { products, methods } = useProducts();
  const [items, setItems] = useState([]);
  const [method, setMethod] = useState("");
  const [discount, setDiscount] = useState("");
  const [client, setClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const cart = Cart({ items, setItems, products });
  const total = items.reduce((s, i) => s + i.price * i.qty, 0);

  const finish = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/pdv/sales", { items: items.map((i) => ({ inventoryId: i.inventoryId, qty: i.qty })), paymentMethod: method, discount: parseFloat(discount) || 0, customerId: client?.id || null });
      toast.success(`Venda ${data.saleNumber} concluída`);
      onDone(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121217] border-gold/20 text-white max-w-xl" data-testid="new-sale-modal">
        <DialogHeader><DialogTitle className="font-display">Nova Venda</DialogTitle><DialogDescription className="text-zinc-500">Produtos do estoque · baixa automática</DialogDescription></DialogHeader>
        <ClientPicker client={client} setClient={setClient} />
        <ProductPicker products={products} featured={cart.featured} onAdd={cart.add} />
        <div className="border-t border-white/10 my-2" />
        <CartList items={items} setQty={cart.setQty} remove={cart.remove} />
        <PayFooter methods={methods} method={method} setMethod={setMethod} total={total} discount={discount} setDiscount={setDiscount} onFinish={finish} saving={saving} disabled={items.length === 0} />
      </DialogContent>
    </Dialog>
  );
}

function ReceiveOSModal({ onClose, onDone }) {
  const { products, methods } = useProducts();
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [os, setOs] = useState(null);
  const [items, setItems] = useState([]);
  const [method, setMethod] = useState("");
  const [discount, setDiscount] = useState("");
  const [saving, setSaving] = useState(false);
  const cart = Cart({ items, setItems, products });

  useEffect(() => { const t = setTimeout(() => api.get(`/pdv/receivable-os?q=${encodeURIComponent(q)}`).then((r) => setList(r.data)), 250); return () => clearTimeout(t); }, [q]);
  const total = (os ? os.chargedAmount : 0) + items.reduce((s, i) => s + i.price * i.qty, 0);

  const finish = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/pdv/sales", { serviceOrderId: os.id, items: items.map((i) => ({ inventoryId: i.inventoryId, qty: i.qty })), paymentMethod: method, discount: parseFloat(discount) || 0 });
      toast.success(`OS ${os.number} concluída · ${data.saleNumber}`);
      onDone(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121217] border-gold/20 text-white max-w-xl" data-testid="receive-os-modal">
        <DialogHeader><DialogTitle className="font-display">Receber Ordem de Serviço</DialogTitle><DialogDescription className="text-zinc-500">Aguardando retirada</DialogDescription></DialogHeader>
        {!os ? (
          <>
            <div className="relative"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input data-testid="os-pdv-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome, número, telefone ou código de retirada..." className="i pl-9" /></div>
            <div className="max-h-72 overflow-y-auto space-y-2 mt-2">
              {list.length === 0 && <p className="text-sm text-zinc-500">Nenhuma OS aguardando retirada.</p>}
              {list.map((o) => (
                <button key={o.id} data-testid={`os-pick-${o.number}`} onClick={() => setOs(o)} className="w-full text-left alpha-card p-3 flex items-center gap-3 hover:border-gold/40">
                  <div className="flex-1"><div className="text-white font-medium">{o.number} · {o.customerName}</div><div className="text-xs text-zinc-500">{o.device?.brand} {o.device?.model} · {o.withdrawalCode}</div></div>
                  <span className="text-gold-bright font-mono-kpi">{money(o.chargedAmount)}</span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="alpha-card p-4 text-sm">
              <div className="flex justify-between"><span className="text-white font-semibold">{os.number} · {os.customerName}</span><button onClick={() => setOs(null)} className="text-zinc-500 hover:text-white"><X size={16} /></button></div>
              <div className="text-zinc-400 mt-1">{os.device?.brand} {os.device?.model} · {os.customerPhone}</div>
              <div className="flex justify-between mt-2"><span className="text-zinc-400">Serviço</span><span className="text-white font-mono-kpi">{money(os.chargedAmount)}</span></div>
            </div>
            <div className="text-xs text-zinc-400 mt-1">Venda adicional (opcional):</div>
            <ProductPicker products={products} featured={cart.featured} onAdd={cart.add} />
            <CartList items={items} setQty={cart.setQty} remove={cart.remove} />
            <PayFooter methods={methods} method={method} setMethod={setMethod} total={total} discount={discount} setDiscount={setDiscount} onFinish={finish} saving={saving} disabled={false} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ExpenseModal({ onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("Geral");
  const [saving, setSaving] = useState(false);
  const CATS = ["Geral", "Fornecedor", "Aluguel", "Salário", "Peça/Insumo", "Marketing", "Outro"];
  const save = async () => {
    if (!(parseFloat(amount) > 0)) { toast.error("Informe um valor maior que zero."); return; }
    if (!description.trim()) { toast.error("Descreva o custo."); return; }
    setSaving(true);
    try {
      await api.post("/pdv/expenses", { amount: parseFloat(amount), description: description.trim(), category });
      toast.success("Custo lançado no caixa");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121217] border-gold/20 text-white max-w-md" data-testid="expense-modal">
        <DialogHeader><DialogTitle className="font-display">Lançar Custo / Despesa</DialogTitle><DialogDescription className="text-zinc-500">Saída de caixa (não é venda)</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><label className="text-xs text-zinc-400 mb-1.5 block">Valor (R$)</label><Input data-testid="expense-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="i" placeholder="0,00" /></div>
          <div><label className="text-xs text-zinc-400 mb-1.5 block">Descrição</label><Input data-testid="expense-description" value={description} onChange={(e) => setDescription(e.target.value)} className="i" placeholder="Ex: Compra de películas do fornecedor" /></div>
          <div><label className="text-xs text-zinc-400 mb-1.5 block">Categoria</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="expense-category" className="i"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121217] border-gold/20 text-white">{CATS.map((c) => <SelectItem key={c} value={c} className="focus:bg-gold/10">{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancelar</Button>
          <Button data-testid="expense-save" onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-500 text-white">Lançar Custo</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GainModal({ onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [method, setMethod] = useState("");
  const [methods, setMethods] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/config/payment-methods").then((r) => setMethods(r.data)); }, []);
  const save = async () => {
    if (!(parseFloat(amount) > 0)) { toast.error("Informe um valor maior que zero."); return; }
    if (!description.trim()) { toast.error("Descreva o ganho."); return; }
    if (!method) { toast.error("Escolha a forma de pagamento."); return; }
    setSaving(true);
    try {
      await api.post("/pdv/gains", { amount: parseFloat(amount), description: description.trim(), paymentMethod: method });
      toast.success("Ganho lançado no caixa (sem nota fiscal)");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121217] border-gold/20 text-white max-w-md" data-testid="gain-modal">
        <DialogHeader><DialogTitle className="font-display">Lançar Ganho</DialogTitle><DialogDescription className="text-zinc-500">Lucro de uma operação/venda não cadastrada. Conta no faturamento e no lucro, mas não emite nota fiscal.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><label className="text-xs text-zinc-400 mb-1.5 block">Valor do ganho (R$)</label><Input data-testid="gain-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="i" placeholder="0,00" /></div>
          <div><label className="text-xs text-zinc-400 mb-1.5 block">Descrição</label><Input data-testid="gain-description" value={description} onChange={(e) => setDescription(e.target.value)} className="i" placeholder="Ex: Comissão de indicação / venda avulsa" /></div>
          <div><label className="text-xs text-zinc-400 mb-1.5 block">Forma de pagamento</label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="gain-method" className="i"><SelectValue placeholder="Pagamento" /></SelectTrigger>
              <SelectContent className="bg-[#121217] border-gold/20 text-white">{methods.map((m) => <SelectItem key={m.key} value={m.key} className="focus:bg-gold/10">{m.label || m.key}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancelar</Button>
          <Button data-testid="gain-save" onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 text-white">Lançar Ganho</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CashSummary() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/pdv/summary").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  return (
    <div className="alpha-card p-6" data-testid="cash-summary">
      <div className="flex items-center gap-2 mb-4"><Receipt size={18} className="text-gold" /><h3 className="font-display font-semibold text-lg text-white">Fechamento de Caixa · Hoje {s.date_label ? `(${s.date_label})` : ""}</h3></div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Kpi label="Total vendido" value={money(s.total_sold)} />
        <Kpi label="Custos (saídas)" value={money(s.total_expenses || 0)} tone="red" />
        <Kpi label="Líquido" value={money(s.net ?? s.total_sold)} tone={(s.net ?? s.total_sold) < 0 ? "red" : "green"} />
        <Kpi label="Vendas" value={s.count} />
        <Kpi label="Lucro bruto" value={money(s.gross_profit)} tone="green" />
      </div>
      {Object.keys(s.by_payment_method).length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(s.by_payment_method).map(([k, v]) => <span key={k} className="text-xs px-3 py-1 rounded-full bg-white/5 text-zinc-300">{k}: <span className="text-gold-bright font-mono-kpi">{money(v)}</span></span>)}
        </div>
      )}
    </div>
  );
}
const Kpi = ({ label, value, tone }) => {
  const color = tone === "green" ? "text-emerald-400" : tone === "red" ? "text-red-400" : "text-white";
  return (<div className="rounded-xl bg-[#0A0A0C] border border-white/5 px-4 py-3"><div className={`text-2xl font-mono-kpi font-extrabold ${color}`}>{value}</div><div className="text-xs text-zinc-500">{label}</div></div>);
};

function SalesHistory({ onReprint }) {
  const { hasPerm } = useAuth();
  const [sales, setSales] = useState([]);
  const [month, setMonth] = useState("");
  const load = () => { const p = month ? `?month=${month}` : ""; api.get(`/pdv/sales${p}`).then((r) => setSales(r.data)); };
  useEffect(() => { load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  const cancel = async (s) => {
    if (!window.confirm(`Cancelar a venda ${s.saleNumber}? O estoque será devolvido${s.serviceOrderNumber ? " e a OS reaberta para retirada" : ""}.`)) return;
    try { await api.post(`/pdv/sales/${s.id}/cancel`); toast.success(`Venda ${s.saleNumber} cancelada`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4" data-testid="sales-history">
      <div className="flex justify-between items-center"><h3 className="font-display font-semibold text-lg text-white">Histórico de Vendas</h3>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="bg-[#121217] border border-white/10 rounded-lg px-3 h-10 text-sm text-white" /></div>
      <div className="grid gap-2">
        {sales.length === 0 && <p className="text-sm text-zinc-500">Nenhuma venda.</p>}
        {sales.map((s) => (
          <div key={s.id} data-testid={`sale-row-${s.saleNumber}`} className="alpha-card p-4 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-white font-medium">{s.saleNumber} {s.customerName ? `· ${s.customerName}` : ""} {s.status === "cancelada" && <span className="text-xs text-red-400">(cancelada)</span>}</div>
              <div className="text-xs text-zinc-500">{s.operatorName} · {s.paymentMethod} · {new Date(s.createdAt).toLocaleString("pt-BR")}</div>
            </div>
            <span className={`font-mono-kpi font-semibold ${s.status === "cancelada" ? "text-zinc-600 line-through" : "text-gold-bright"}`}>{money(s.total)}</span>
            <Button data-testid={`reprint-${s.saleNumber}`} size="sm" variant="outline" className="border-gold/30 text-gold-bright hover:bg-gold/10" onClick={() => onReprint(s)}><Printer size={14} className="mr-1" />Reimprimir</Button>
            {s.status === "concluida" && hasPerm("pdv.finalize") && (
              <Button data-testid={`cancel-${s.saleNumber}`} size="sm" variant="outline" className="border-red-500/30 text-red-300 hover:bg-red-500/10" onClick={() => cancel(s)}><Ban size={14} className="mr-1" />Cancelar</Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
